import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { propFirmData } from '../data/propFirmDataHomepage'
import { FilterBar } from '../components/FilterBar';
import { ComparisonTable } from '../components/ComparisonTable';

export function ComparePage() {
  const [sortKey, setSortKey] = useState<'maxFunding' | 'profitSplit' | 'rating'>('rating');
  const [view, setView] = useState<'grid' | 'table'>('table');
  const [filters, setFilters] = useState({
    search: '',
    evaluation: [],
    platforms: [],
    payout: [],
    drawdown: [],
    profitSplit: [],
    maxFunding: []
  });

  const firms = useMemo(() => {
    return Object.values(propFirmData)
      .filter(firm => {
        const matchesSearch = firm.name.toLowerCase().includes(filters.search.toLowerCase());

        const matchesFilters = ['evaluation', 'platforms', 'payout', 'drawdown', 'profitSplit', 'maxFunding'].every(key => {
          if (filters[key].length === 0) return true;

          const firmValues = (() => {
            switch (key) {
              case 'evaluation': return firm.evaluation?.types || [];
              case 'platforms': return firm.features?.platforms || [];
              case 'payout': return firm.features?.payout ? [firm.features.payout] : [];
              case 'drawdown': return firm.features?.maxDrawdown ? [firm.features.maxDrawdown.split(',')[0].trim()] : [];
              case 'profitSplit': return firm.features?.profitSplit ? [firm.features.profitSplit] : [];
              case 'maxFunding': return firm.features?.maxFunding ? [firm.features.maxFunding] : [];
              default: return [];
            }
          })();

          return filters[key].every(f => firmValues.includes(f));
        });

        return matchesSearch && matchesFilters;
      })
      .filter(firm => firm.features && firm.evaluation)
      .sort((a, b) => {
        if (sortKey === 'rating') return b.rating - a.rating;
        if (sortKey === 'maxFunding') {
          const aVal = parseInt(a.features.maxFunding.replace(/[$,]/g, ''));
          const bVal = parseInt(b.features.maxFunding.replace(/[$,]/g, ''));
          return bVal - aVal;
        }
        if (sortKey === 'profitSplit') {
          const aVal = parseInt(a.features.profitSplit.replace(/[^0-9]/g, ''));
          const bVal = parseInt(b.features.profitSplit.replace(/[^0-9]/g, ''));
          return bVal - aVal;
        }
        return 0;
      });
  }, [sortKey, filters]);

  return (
    <>
      <Helmet>
        <title>Compare Top Prop Trading Firms | PropViper</title>
        <meta
          name="description"
          content="Compare the best prop trading firms side by side. See funding options, profit splits, and trading conditions to find your perfect match."
        />
      </Helmet>

      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden py-16 md:py-24">
        <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover opacity-20">
          <source src="/assets/compare-hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-black/70" />
        <div className="relative z-10 container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-yellow-400 leading-tight mb-4">
            Compare Top Prop Trading Firms
          </h1>
          <p className="text-lg text-yellow-600 max-w-2xl mx-auto">
            Side-by-side breakdown of max funding, profit splits, time limits, platforms, and exclusive promos — all in one place.
          </p>
        </div>
      </section>

      <div className="bg-gray-950 border-b border-yellow-800 sticky top-[64px] z-10 shadow-md">
        <div className="max-w-6xl mx-auto px-4 py-4 flex flex-wrap justify-between items-center gap-4">
          <div className="flex gap-2">
            <span className="text-sm font-medium text-yellow-200">Sort by:</span>
            {['rating', 'maxFunding', 'profitSplit'].map((key) => (
              <button
                key={key}
                onClick={() => setSortKey(key as any)}
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  sortKey === key ? 'bg-yellow-400 text-black' : 'bg-white/10 text-yellow-300 hover:bg-white/20'
                }`}
              >
                {key === 'rating' ? '⭐ Rating' : key === 'maxFunding' ? '💰 Max Funding' : '📈 Profit Split'}
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setView('table')}
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                view === 'table' ? 'bg-yellow-400 text-black' : 'bg-white/10 text-yellow-300 hover:bg-white/20'
              }`}
            >
              📊 Table View
            </button>
            <button
              onClick={() => setView('grid')}
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                view === 'grid' ? 'bg-yellow-400 text-black' : 'bg-white/10 text-yellow-300 hover:bg-white/20'
              }`}
            >
              🗂️ Grid View
            </button>
          </div>
        </div>
      </div>

      <section className="bg-gray-950 text-yellow-100 py-16">
        <FilterBar filters={filters} onChange={setFilters} firms={Object.values(propFirmData)} />

        <div className="max-w-6xl mx-auto px-4 mt-6">
          {firms.length === 0 && (
            <div className="text-center text-yellow-300 py-16">
              No matching firms found. Try adjusting your filters.
            </div>
          )}

          {view === 'table' ? (
            <ComparisonTable firms={firms} variant="compare" />
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {firms.map((firm) => (
                <div key={firm.id} className="bg-white/10 backdrop-blur rounded-xl shadow-md p-6">
                  {firm.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export default ComparePage;
